/**
 * 워크스페이스 자격. 사람과 팀의 정본은 카탈로그 인력·조직이고,
 * 여기에는 역할·유형·상태만 얹는다.
 */

export const DIRECTORY_SELF_ID = "res_me";
export const DIRECTORY_ADMIN_ID = "res_kim_pm";
/** 그로스 마케팅 목업. 초대를 보냈고 아직 들어오지 않은 사람. */
export const DIRECTORY_INVITED_ID = "res_cho_partner";

export type DirectoryRole = "owner" | "admin" | "editor" | "viewer";
export type DirectoryKind = "member" | "guest";
export type DirectoryStatus = "active" | "invited" | "suspended" | "removed";

export type DirectoryGrant = {
  role: DirectoryRole;
  kind: DirectoryKind;
  status: DirectoryStatus;
  lastActive?: string;
};

export const DIRECTORY_ROLE_LABEL: Record<DirectoryRole, string> = {
  owner: "소유자",
  admin: "관리자",
  editor: "편집자",
  viewer: "뷰어",
};

/** 역할 메뉴의 한 줄. 권한 표가 자세한 차이를 말하므로 여기에는 고르는 차이만 둔다. */
export const DIRECTORY_ROLE_LINE: Record<Exclude<DirectoryRole, "owner">, string> = {
  admin: "멤버와 설정을 바꿉니다",
  editor: "일정과 작업을 만들고 고칩니다",
  viewer: "공개된 내용만 읽습니다",
};

export const DIRECTORY_STATUS_LABEL: Record<DirectoryStatus, string> = {
  active: "활성",
  invited: "초대 중",
  suspended: "중지",
  removed: "제거됨",
};

const ROLES: DirectoryRole[] = ["owner", "admin", "editor", "viewer"];
const KINDS: DirectoryKind[] = ["member", "guest"];
const STATUSES: DirectoryStatus[] = ["active", "invited", "suspended", "removed"];

/** 목업에서 최근 활동이 있는 사람. 시간은 지금 기준 몇 시간 전. 없는 사람은 「—」. */
const SAMPLE_LAST_ACTIVE_HOURS: Record<string, number> = {
  res_me: 2,
  res_kim_pm: 4,
  res_lim_growth: 5,
  res_han_robot: 6,
  res_yoon_data: 8,
  res_kwon_crm: 20,
  res_jung_front: 30,
  res_min_qa: 52,
};

function withSampleActivity(resourceId: string, grant: DirectoryGrant): DirectoryGrant {
  if (grant.lastActive || grant.status !== "active") return grant;
  const hours = SAMPLE_LAST_ACTIVE_HOURS[resourceId];
  if (hours == null) return grant;
  return { ...grant, lastActive: new Date(Date.now() - hours * 3_600_000).toISOString() };
}

export function defaultGrant(resourceId: string): DirectoryGrant {
  if (resourceId === DIRECTORY_SELF_ID) return { role: "owner", kind: "member", status: "active" };
  if (resourceId === DIRECTORY_ADMIN_ID) return { role: "admin", kind: "member", status: "active" };
  if (resourceId === DIRECTORY_INVITED_ID) return { role: "viewer", kind: "member", status: "invited" };
  return { role: "editor", kind: "member", status: "active" };
}

export function grantOf(stored: Record<string, DirectoryGrant>, resourceId: string): DirectoryGrant {
  if (resourceId === DIRECTORY_SELF_ID) {
    const lastActive = stored[resourceId]?.lastActive;
    return withSampleActivity(resourceId, lastActive ? { ...defaultGrant(resourceId), lastActive } : defaultGrant(resourceId));
  }
  return withSampleActivity(resourceId, stored[resourceId] ?? defaultGrant(resourceId));
}

/** 소유자 행과 소유자로의 변경은 거절한다. 같은 역할이면 그대로 둔다. */
export function changeDirectoryRole(grant: DirectoryGrant, role: DirectoryRole): DirectoryGrant | null {
  if (grant.role === "owner" || role === "owner") return null;
  if (grant.role === role) return grant;
  return { ...grant, role };
}

/** 인력 기록은 남기고 자격만 제거로 둔다. 소유자는 빼지 않는다. */
export function removeDirectoryGrant(grant: DirectoryGrant): DirectoryGrant | null {
  if (grant.role === "owner") return null;
  if (grant.status === "removed") return grant;
  return { ...grant, status: "removed" };
}

export function invitedGrant(role: Exclude<DirectoryRole, "owner">, kind: DirectoryKind): DirectoryGrant {
  return { role, kind, status: "invited" };
}

export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function findPersonByEmail(people: { id: string; email: string }[], email: string): string | null {
  const key = normalizeEmail(email);
  if (!key.includes("@")) return null;
  return people.find((person) => normalizeEmail(person.email) === key)?.id ?? null;
}

/** 없는 사람은 만들고, 제거된 사람은 다시 초대한다. 이미 있으면 막는다. */
export function inviteOutcome(existing: DirectoryGrant | null): "create" | "revive" | "already" {
  if (!existing) return "create";
  if (existing.status === "removed") return "revive";
  return "already";
}

/** 한 사람의 소속 팀은 하나다. 같으면 null. 빈 값은 소속 없음이다. */
export function nextTeamId(current: string | undefined, next: string | undefined): string | undefined | null {
  const from = current || undefined;
  const to = next || undefined;
  if (from === to) return null;
  return to;
}

export type TeamRosterPerson = {
  id: string;
  name: string;
  teamId?: string;
  teamName?: string;
  status: DirectoryStatus;
  kind: DirectoryKind;
};

export type TeamRosterChoice = {
  id: string;
  name: string;
  /** 다른 팀 이름. 이 팀이거나 없으면 비운다. */
  teamName?: string;
};

/** 넣을 수 있는 활성 멤버와, 이미 이 팀에 있는 사람. */
export function teamFormRoster(people: TeamRosterPerson[], teamId?: string): { choices: TeamRosterChoice[]; memberIds: string[] } {
  const listed = people.filter((person) => {
    if (person.status === "removed") return false;
    if (person.status === "active" && person.kind === "member") return true;
    return teamId != null && person.teamId === teamId;
  });
  return {
    choices: listed.map((person) => ({
      id: person.id,
      name: person.name,
      teamName: person.teamId && person.teamId !== teamId ? person.teamName || undefined : undefined,
    })),
    memberIds: teamId ? listed.filter((person) => person.teamId === teamId).map((person) => person.id) : [],
  };
}

/** 대화상자에서 고른 소속만 반영한다. 명단에 없는 사람은 그대로 둔다. */
export function teamMembershipUpdates(
  rows: { id: string; teamId?: string }[],
  teamId: string,
  memberIds: readonly string[],
  scopeIds: readonly string[],
): { id: string; teamId?: string }[] {
  const wanted = new Set(memberIds);
  const scope = new Set(scopeIds);
  const updates: { id: string; teamId?: string }[] = [];
  for (const row of rows) {
    if (!scope.has(row.id)) continue;
    if (wanted.has(row.id)) {
      const next = nextTeamId(row.teamId, teamId);
      if (next !== null) updates.push({ id: row.id, teamId: next });
    } else if (row.teamId === teamId) {
      updates.push({ id: row.id, teamId: undefined });
    }
  }
  return updates;
}

export function isDirectoryRole(value: unknown): value is DirectoryRole {
  return typeof value === "string" && ROLES.includes(value as DirectoryRole);
}

export function isDirectoryKind(value: unknown): value is DirectoryKind {
  return typeof value === "string" && KINDS.includes(value as DirectoryKind);
}

export function isDirectoryStatus(value: unknown): value is DirectoryStatus {
  return typeof value === "string" && STATUSES.includes(value as DirectoryStatus);
}

export function readGrant(value: unknown): DirectoryGrant | null {
  if (!value || typeof value !== "object") return null;
  const row = value as Record<string, unknown>;
  if (!isDirectoryRole(row.role) || !isDirectoryKind(row.kind) || !isDirectoryStatus(row.status)) return null;
  if (row.role === "owner") return null;
  const lastActive = typeof row.lastActive === "string" ? row.lastActive : undefined;
  return { role: row.role, kind: row.kind, status: row.status, ...(lastActive ? { lastActive } : {}) };
}

export type WorkPlan = {
  id: string;
  name: string;
  lifecycle?: "active" | "done" | "archived" | "trashed";
  assignments: { resourceId: string; projectRole: string }[];
  tasks: { id: string; title: string; status?: string; due?: string; assignees: string[] }[];
};

export type OpenWorkTask = {
  id: string;
  projectId: string;
  projectName: string;
  title: string;
  status?: string;
  due?: string;
  overdue: boolean;
};

export type PersonWork = {
  activePlans: { id: string; name: string; role: string }[];
  openTasks: OpenWorkTask[];
  doneCount: number;
  overdueCount: number;
};

function planOpen(plan: WorkPlan): boolean {
  return plan.lifecycle !== "archived" && plan.lifecycle !== "trashed";
}

/** 진행 중 플랜은 배정, 작업 수는 담당 이름이다. */
export function personWork(plans: WorkPlan[], resourceId: string, name: string, today: string): PersonWork {
  const activePlans: PersonWork["activePlans"] = [];
  const openTasks: OpenWorkTask[] = [];
  let doneCount = 0;
  for (const plan of plans) {
    if (!planOpen(plan)) continue;
    const assignment = plan.assignments.find((row) => row.resourceId === resourceId);
    if (assignment) activePlans.push({ id: plan.id, name: plan.name, role: assignment.projectRole });
    for (const task of plan.tasks) {
      if (!task.assignees.includes(name)) continue;
      if (task.status === "done") {
        doneCount += 1;
        continue;
      }
      const overdue = !!task.due && task.due < today;
      openTasks.push({
        id: task.id,
        projectId: plan.id,
        projectName: plan.name,
        title: task.title,
        status: task.status,
        due: task.due,
        overdue,
      });
    }
  }
  openTasks.sort((a, b) => (a.due ?? "9999").localeCompare(b.due ?? "9999"));
  return {
    activePlans,
    openTasks,
    doneCount,
    overdueCount: openTasks.filter((task) => task.overdue).length,
  };
}

export type GrantPerson = {
  id: string;
  name: string;
  email: string;
  teamId?: string;
  status: DirectoryStatus;
  kind: DirectoryKind;
};

/** 플랜 명단에 없고 활성인 워크스페이스 멤버. */
export function planAddCandidates(people: GrantPerson[], rosterEmails: string[]): GrantPerson[] {
  const have = new Set(rosterEmails.map(normalizeEmail));
  return people.filter(
    (person) => person.status === "active" && person.kind === "member" && person.email.includes("@") && !have.has(normalizeEmail(person.email)),
  );
}

/** 팀에 있는 활성 멤버 중 명단에 없는 사람. 역할은 편집자다. */
export function teamGrantRows(people: GrantPerson[], teamId: string, rosterEmails: string[]): { name: string; email: string; role: string }[] {
  const have = new Set(rosterEmails.map(normalizeEmail));
  return people
    .filter(
      (person) =>
        person.teamId === teamId &&
        person.status === "active" &&
        person.kind === "member" &&
        person.email.includes("@") &&
        !have.has(normalizeEmail(person.email)),
    )
    .map((person) => ({ name: person.name, email: person.email, role: "편집자" }));
}

/** 열린 플랜에 배정되어 있으면 그 플랜, 하나뿐이면 그 플랜, 여러 개면 고른다. */
export function assignmentTarget(planIds: string[], activePlanId: string | null): "none" | "pick" | { planId: string } {
  if (planIds.length === 0) return "none";
  if (activePlanId && planIds.includes(activePlanId)) return { planId: activePlanId };
  if (planIds.length === 1) return { planId: planIds[0]! };
  return "pick";
}
